import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Search, ClipboardList, Phone, Bell, Key, AlertTriangle,
  CheckCircle, Clock, Users, ArrowRight, Home, Menu, X, LogOut,
  QrCode, UserCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { securityAPI } from '../services/api';
import { BackHeader } from '../components/BackHeader';

interface Alert {
  id: string;
  type: 'emergency' | 'visitor' | 'maintenance' | 'system';
  message: string;
  timestamp: string;
  priority: 'high' | 'medium' | 'low';
}

interface VisitorLog {
  id: string;
  visitorName: string;
  residentName: string;
  unit: string;
  entryTime: string;
  exitTime?: string;
  status: 'in' | 'out';
}

interface Alert {
  id: string;
  type: 'emergency' | 'visitor' | 'maintenance' | 'system';
  message: string;
  timestamp: string;
  priority: 'high' | 'medium' | 'low';
}

interface VisitorLog {
  id: string;
  visitorName: string;
  residentName: string;
  unit: string;
  entryTime: string;
  exitTime?: string;
  status: 'in' | 'out';
}

export default function SecurityDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeAlert, setActiveAlert] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [visitors, setVisitors] = useState<VisitorLog[]>([]);

  useEffect(() => {
    loadData();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadData = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      const [alertsRes, visitorsRes] = await Promise.all([
        securityAPI.getAlerts(condoId),
        securityAPI.getVisitors(condoId)
      ]);
      // Bug #223 (2026-09-30): /security/condo/:id/alerts and /visitors always
      // return an envelope ({ success, data, error }) - on 200s AND on error
      // objects (500/410/403). res.data || res || [] set that envelope OBJECT
      // as list state, so alerts/visitors were never arrays and the
      // alerts.filter / visitors.filter calls below crashed with
      // "d.filter is not a function". Unwrap the envelope and guard with
      // Array.isArray (default []) at the data boundary - same convention as
      // Visitors.tsx:89-90 / ReporteVisitas.tsx:44-45.
      const alertsPayload = alertsRes?.data?.data ?? alertsRes?.data ?? [];
      setAlerts(Array.isArray(alertsPayload) ? alertsPayload : []);
      const visitorsPayload = visitorsRes?.data?.data ?? visitorsRes?.data ?? [];
      setVisitors(Array.isArray(visitorsPayload) ? visitorsPayload : []);
    } catch (err) {
      console.error('Error loading security data:', err);
      // Keep empty arrays on error
    }
  };

  const activeVisitors = visitors.filter(v => v.status === 'in');
  const highPriorityAlerts = alerts.filter(a => a.priority === 'high');

  const handleVerifyQR = () => {
    navigate('/verify');
  };

  const handleVisitorLog = () => {
    navigate('/reporte-visitas');
  };

  const handleEmergency = () => {
    navigate('/emergencias');
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <BackHeader fallback="/" />
      {/* Header */}
      <header className="sticky top-0 bg-[#1a365d] text-white z-50">
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-white/10 rounded-xl">
              <Menu className="w-6 h-6" />
            </button>
            <Shield className="w-8 h-8" />
            <div>
              <h1 className="font-bold text-lg">Panel de Seguridad</h1>
              <p className="text-xs text-white/70">{user?.name || 'Guardia'}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-2xl font-bold">{currentTime.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</p>
              <p className="text-xs text-white/70">{currentTime.toLocaleDateString('es-MX')}</p>
            </div>
            
            <div className="relative">
              <Bell className="w-6 h-6" />
              {highPriorityAlerts.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
                  {highPriorityAlerts.length}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="w-64 bg-[#1a365d] text-white h-full p-4">
            <div className="flex items-center justify-between mb-6">
              <span className="font-bold text-lg">Menú</span>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <nav className="space-y-2">
              <button onClick={() => { navigate('/home/dashboard'); setSidebarOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/10">
                <Home className="w-5 h-5" /> Dashboard
              </button>
              <button onClick={() => { handleVerifyQR(); setSidebarOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/10">
                <QrCode className="w-5 h-5" /> Verificar QR
              </button>
              <button onClick={() => { handleVisitorLog(); setSidebarOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/10">
                <ClipboardList className="w-5 h-5" /> Registro Visitas
              </button>
              <button onClick={() => { handleEmergency(); setSidebarOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/10">
                <Phone className="w-5 h-5" /> Emergencias
              </button>
              <button onClick={() => { navigate('/llave-digital'); setSidebarOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/10">
                <Key className="w-5 h-5" /> Llave Digital
              </button>
              <button onClick={() => { navigate('/home/edicion-perfil'); setSidebarOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/10">
                <UserCircle className="w-5 h-5" /> Mi Perfil
              </button>
            </nav>
            
            <div className="absolute bottom-4 left-4 right-4">
              <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/10 text-red-300">
                <LogOut className="w-5 h-5" /> Cerrar Sesión
              </button>
            </div>
          </div>
          <div className="flex-1 bg-black/50" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      <main className="p-4 max-w-6xl mx-auto space-y-4">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button onClick={handleVerifyQR} className="bg-[#36CCD8] text-white p-4 rounded-2xl flex flex-col items-center gap-2 hover:bg-[#2DB8C4] transition-all">
            <QrCode className="w-8 h-8" />
            <span className="font-bold text-sm">Verificar QR</span>
          </button>
          
          <button onClick={handleVisitorLog} className="bg-emerald-500 text-white p-4 rounded-2xl flex flex-col items-center gap-2 hover:bg-emerald-600 transition-all">
            <ClipboardList className="w-8 h-8" />
            <span className="font-bold text-sm">Registro Visitas</span>
          </button>
          
          <button onClick={handleEmergency} className="bg-red-500 text-white p-4 rounded-2xl flex flex-col items-center gap-2 hover:bg-red-600 transition-all">
            <Phone className="w-8 h-8" />
            <span className="font-bold text-sm">Emergencias</span>
          </button>
          
          <button onClick={() => navigate('/llave-digital')} className="bg-amber-500 text-white p-4 rounded-2xl flex flex-col items-center gap-2 hover:bg-amber-600 transition-all">
            <Key className="w-8 h-8" />
            <span className="font-bold text-sm">Llave Digital</span>
          </button>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-[#36CCD8]" />
              <span className="text-sm text-gray-500">Visitantes Activos</span>
            </div>
            <p className="text-3xl font-bold text-[#2D3748]">{activeVisitors.length}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-sm text-gray-500">Alertas Críticas</span>
            </div>
            <p className="text-3xl font-bold text-red-500">{highPriorityAlerts.length}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="text-sm text-gray-500">Códigos Válidos</span>
            </div>
            <p className="text-3xl font-bold text-[#2D3748]">12</p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-amber-500" />
              <span className="text-sm text-gray-500">Turno</span>
            </div>
            <p className="text-lg font-bold text-[#2D3748]">Diurno</p>
          </div>
        </div>

        {/* Active Visitors */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-bold text-[#2D3748]">Visitantes Dentro</h2>
            <button onClick={handleVisitorLog} className="text-sm text-[#36CCD8] font-medium flex items-center gap-1">
              Ver todo <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {activeVisitors.map(visitor => (
              <div key={visitor.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-bold text-[#2D3748]">{visitor.visitorName}</p>
                  <p className="text-sm text-gray-500">Visita a: {visitor.residentName} ({visitor.unit})</p>
                </div>
                <div className="text-right">
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                    Dentro
                  </span>
                  <p className="text-xs text-gray-400 mt-1">Entrada: {visitor.entryTime}</p>
                </div>
              </div>
            ))}
            {activeVisitors.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-emerald-400" />
                <p>No hay visitantes activos</p>
              </div>
            )}
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="font-bold text-[#2D3748]">Alertas Activas</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {alerts.map(alert => (
              <div 
                key={alert.id} 
                className={`p-4 flex items-start gap-3 ${alert.priority === 'high' ? 'bg-red-50' : ''}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  alert.priority === 'high' ? 'bg-red-500' : 
                  alert.priority === 'medium' ? 'bg-amber-500' : 'bg-gray-400'
                }`}>
                  {alert.type === 'emergency' ? <AlertTriangle className="w-5 h-5 text-white" /> :
                   alert.type === 'visitor' ? <Users className="w-5 h-5 text-white" /> :
                   alert.type === 'maintenance' ? <Home className="w-5 h-5 text-white" /> :
                   <Bell className="w-5 h-5 text-white" />}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#2D3748]">{alert.message}</p>
                  <p className="text-xs text-gray-500">{alert.timestamp}</p>
                </div>
                <button 
                  onClick={() => setActiveAlert(activeAlert === alert.id ? null : alert.id)}
                  className="text-sm text-[#36CCD8] font-medium"
                >
                  {activeAlert === alert.id ? 'Cerrar' : 'Atender'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Emergency Numbers Quick Access */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <h3 className="font-bold text-red-700 mb-3 flex items-center gap-2">
            <Phone className="w-5 h-5" /> Números de Emergencia
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <a href="tel:911" className="bg-white rounded-xl p-3 text-center hover:bg-red-100 transition-colors">
              <p className="text-2xl font-bold text-red-600">911</p>
              <p className="text-xs text-gray-600">Emergencias</p>
            </a>
            <a href="tel:065" className="bg-white rounded-xl p-3 text-center hover:bg-red-100 transition-colors">
              <p className="text-2xl font-bold text-red-600">065</p>
              <p className="text-xs text-gray-600">Bomberos</p>
            </a>
            <a href="tel:5551234567" className="bg-white rounded-xl p-3 text-center hover:bg-red-100 transition-colors">
              <p className="text-lg font-bold text-red-600">Administración</p>
              <p className="text-xs text-gray-600">555-123-4567</p>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
